/**
 * Recall — Bloq's unified post-meeting record area.
 *
 * Top-level page at /app/recall lists every meeting in the workspace that has
 * Recall data attached. Each row links to the meeting detail page where the
 * Recall sub-panel renders all the underlying data (notes, transcript, AI
 * summary, follow-up, tasks, participants, recordings, Recall Report export).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, FileText, Users, CheckSquare, ChevronRight, Download, Search, X as XIcon } from "lucide-react";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatLongDateTimeInTz, userBrowserTimezone } from "@/lib/time";

export default function Recall() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const tz = user?.timezone || userBrowserTimezone();

  useEffect(() => {
    if (!currentWorkspace) return;
    setLoading(true);
    (async () => {
      try {
        // Pull all meetings for the workspace, then in parallel pull summary/notes/task counts.
        const mtgs = await api.get(`/meetings`, { params: { workspace_id: currentWorkspace.workspace_id } });
        const meetings = mtgs.data || [];
        const summaries = await api.get(`/summaries`, { params: { workspace_id: currentWorkspace.workspace_id } });
        const sumByMtg = Object.fromEntries((summaries.data || []).map((s) => [s.meeting_id, s]));
        const enriched = meetings
          .map((m) => ({ ...m, _summary: sumByMtg[m.meeting_id] }))
          .filter((m) => m.status === "completed" || m.status === "ended" || m._summary || m.has_recording)
          .sort((a, b) => {
            // Newest first: actual_end → actual_start → start_time → created_at
            const da = a.actual_end || a.actual_start || a.start_time || a.created_at || "";
            const db = b.actual_end || b.actual_start || b.start_time || b.created_at || "";
            return db.localeCompare(da);
          });
        setRows(enriched);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentWorkspace]);

  // Per-page search across title, summary, action items, decisions, participants
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const fields = [
        r.title || "",
        r.description || "",
        r._summary?.summary_text || "",
        ...(r._summary?.action_items || []).map((it) => (typeof it === "string" ? it : it?.title || "")),
        ...(r._summary?.decisions || []).map((it) => (typeof it === "string" ? it : it?.decision_text || "")),
        ...(r.invited_emails || []),
      ];
      return fields.some((f) => String(f).toLowerCase().includes(q));
    });
  }, [rows, search]);

  return (
    <div className="flex flex-col gap-6" data-testid="recall-page">
      <header>
        <p className="bloq-eyebrow">Recall</p>
        <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Every meeting, remembered.</h1>
        <p className="text-sm text-[#82828c] mt-3 max-w-2xl">
          Recall is your post-meeting memory: notes, transcript, AI summary, follow-up, tasks,
          participants, recordings — and one-click Recall Report exports as DOCX or PDF.
        </p>
      </header>

      {/* Per-page search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-[#82828c] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search Recall: title, summary, actions, decisions…"
          className="bloq-input pl-9 pr-9 w-full"
          data-testid="recall-search-input"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#f5efe6]"
            data-testid="recall-search-clear"
            aria-label="Clear search"
          >
            <XIcon className="w-3.5 h-3.5 text-[#82828c]" />
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-[#82828c]">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bloq-card p-10 text-center">
          <Sparkles className="w-8 h-8 mx-auto text-[#82828c]" />
          <p className="bloq-headline text-xl mt-3 mb-1 text-[#005e7a]">
            {search ? "No matching Recall records" : "Nothing in Recall yet"}
          </p>
          <p className="text-sm text-[#82828c]">
            {search
              ? `No Recall records match "${search}".`
              : "After your first completed meeting, you'll find its full record here."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((r) => (
            <Link
              key={r.meeting_id}
              to={`/app/meetings/${r.meeting_id}#recall`}
              className="bloq-card p-5 hover:-translate-y-0.5 transition-transform flex items-start justify-between gap-3"
              data-testid={`recall-row-${r.meeting_id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="bloq-headline text-lg truncate text-[#005e7a]">{r.title || "Untitled meeting"}</p>
                  <StatusPill status={r.status} />
                </div>
                <p className="text-xs text-[#82828c] mt-1">
                  {formatLongDateTimeInTz(r.actual_end || r.start_time, tz)}
                </p>
                {r._summary?.summary_text && (
                  <p className="text-sm text-[#4a4a52] mt-2 line-clamp-2">{r._summary.summary_text}</p>
                )}
                <div className="flex gap-3 mt-3 text-xs text-[#82828c]">
                  {r._summary?.action_items?.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <CheckSquare className="w-3 h-3" /> {r._summary.action_items.length} actions
                    </span>
                  )}
                  {r._summary?.decisions?.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <FileText className="w-3 h-3" /> {r._summary.decisions.length} decisions
                    </span>
                  )}
                  {r.has_recording && (
                    <span className="inline-flex items-center gap-1 text-[#005e7a] font-medium">
                      <Download className="w-3 h-3" /> recording
                    </span>
                  )}
                  {r.ai_summary_enabled === false && (
                    <span className="inline-flex items-center gap-1 text-[#82828c]">AI off</span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#82828c] flex-shrink-0 mt-1" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    completed: { bg: "#16a34a1a", c: "#16a34a", label: "Completed" },
    live: { bg: "#005e7a1a", c: "#005e7a", label: "Live" },
    scheduled: { bg: "#82828c1a", c: "#82828c", label: "Scheduled" },
    canceled: { bg: "#c438381a", c: "#c43838", label: "Canceled" },
    ended: { bg: "#16a34a1a", c: "#16a34a", label: "Ended" },
  };
  const s = map[status] || { bg: "#82828c1a", c: "#82828c", label: status || "—" };
  return (
    <span
      className="inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold"
      style={{ background: s.bg, color: s.c }}
    >
      {s.label}
    </span>
  );
}
